import React, { useMemo, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useInView } from 'react-intersection-observer'
import { useQuery } from 'jsonapi-react'
import posthog from 'posthog-js'
import { Layout, Header } from 'app/theme/layout'
import NavbarFixed from 'app/theme/NavbarFixed'
import {
  FoodTruckCatering,
  SaveTime,
  MakeEveryoneHappy,
  SitBackRelax,
  RepeatRepeat,
} from 'app/theme/sections'
import NotFoundPage from 'app/pages/NotFoundPage'
import WhyWorkWithUs from '../theme/sections/WhyWorkWithUs'
import CateringForAnyEvent from '../theme/sections/CateringForAnyEvent'
import HowItWorksLarge from '../theme/sections/HowItWorksLarge'
import HowItWorks from '../theme/sections/HowItWorks'
import FeaturedClients from '../theme/sections/FeaturedClients'
import Calculator from 'app/theme/calculator'
import LeadModal from 'app/leads/LeadModal'
import { useModalState } from 'lib/modal'
import CatererList from './cuisine/CatererList'
import CuisineTypes from 'app/theme/sections/CuisineTypes'
import config from './cuisine/config'
import styles from './CuisinePage.module.css'

const menuItems = [
  {
    title: 'Why Work With Us',
    url: `#why-work-with-us`,
  },
  {
    title: 'How It Works',
    url: `#how-it-works`,
  },
  {
    title: 'Cost Calculator',
    url: `#calculator`,
  },
  {
    title: 'FAQ',
    url: `#faq`,
  },
]

export default function CuisinePage(props) {
  const params = useParams()
  const data = config.pages.find(i => i.slug === params.cuisine)

  const { openModal, ...modalState } = useModalState()

  const [ref, inView] = useInView({
    rootMargin: '1px',
    initialInView: true,
  })

  useEffect(() => {
    posthog.startSessionRecording()
    return () => {
      posthog.stopSessionRecording()
    }
  }, [])

  if (params.cuisine && !data) {
    return <NotFoundPage />
  }


  const tagQuery = useQuery([
    'tags',
    {
      filter: {
        slug: data.tag_slug,
      },
      include: ['photos'],
      fields: {
        tags: ['id', 'name', 'description'],
        photos: ['path', 'uuid', 'extension'],
      },
      page: {
        limit: 1,
      },
    },
  ])

  const tag = tagQuery.data && tagQuery.data[0]

  const { data: caterers } = useQuery(
    tag && [
      'caterers',
      {
        filter: {
          tag_id: tag.id,
          closed: false,
        },
        include: ['photo', 'tags', 'cities'],
        fields: {
          caterers: [
            'id',
            'name',
            'slug',
            'description',
            'short_description',
            'ranking_tier',
          ],
          photos: ['uuid', 'extension', 'path'],
          tags: ['name'],
          cities: ['name']
        },
        sort: ['-ranking'],
        page: {
          size: 8,
        },
      },
    ]
  )

  const googleMetaTags = useMemo(() => {
    return JSON.stringify({
      "@context": "https:\/\/schema.org",
      "@type": "FoodService",
      "subjectOf": {
        "@type": "FAQPage",
        "mainEntity":  data?.faq?.slice(0,5).map((q) => {
          return {
            '@type': 'Question',
            name: q.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: q.answer
            }
          }
        }),
        'name': `${data.tag_slug} Truck Catering FAQ`,
        '@id': `https://roaminghunger.com/food-truck-catering/${data.slug}/#FAQPage`
      },
      'additionalType': [
        'https://www.wikidata.org/wiki/Q3303463',
        'https://www.wikidata.org/wiki/Q6887072'
      ],
      'image': [
        data.slides.save_time.image,
        data.slides.make_everyone_happy.image,
        data.slides.sit_back_relax.image,
        data.slides.repeat.image,
        data.how_it_works_image
      ],
      'provider': caterers?.slice(0,2).map(caterer => ({
        '@type': 'Restaurant',
        url: `https://roaminghunger.com/${caterer.slug}/`,
        name: caterer.name,
        '@id': `https://roaminghunger.com/${caterer.slug}/#Restaurant`,
      })),
      'url': `https://roaminghunger.com/food-truck-catering/${data.slug}/`,
      'name': data.seo_title,
      'description': `Hire the right ${data.tag_slug} truck for your next catered event`,
      '@id': `https://roaminghunger.com/food-truck-catering/${data.slug}/#FoodService`

    })
  }, [data, caterers])

  return (
    <Layout header={<Header occasionMenu />}>
      <Helmet>
        <title>{data?.seo_title}</title>
        <meta name="description" content={data?.seo_description}></meta>
        <meta name="og:title" content={data?.seo_title} />
        <script type="application/ld+json">{googleMetaTags}</script>
      </Helmet>
      <LeadModal {...modalState} />
      <div ref={ref}>
        <FoodTruckCatering
          title={data.title}
          description={`Hire the right ${data.description_term} for your next catered event`}
          imageUrl={data.image}
          jumpTarget="#save-time"
          onButtonClick={openModal}
        />
      </div>
      <NavbarFixed
        bgColor="bg-blue"
        className="text-white font-normal text-md"
        buttonStyle="text-white border-white border-2"
        onButtonClick={openModal}
        menuItems={menuItems}
      />
      <div className="container">
        <h3 className="font-platform leading-none text-navy text-4xl text-center my-12">{`Find the Best ${data.title} Near You`}</h3>
      </div>
      <CatererList caterers={caterers} />
      <SaveTime
        id="save-time"
        className={data.slides.save_time?.textColor}
        imageUrl={data.slides.save_time.image}
        heading={data.slides.save_time.title}
        description={data.slides.save_time.description}
        jumpTarget="#make-everyone-happy"
        onButtonClick={openModal}
      />
      <MakeEveryoneHappy
        id="make-everyone-happy"
        className={data.slides.make_everyone_happy?.textColor}
        imageUrl={data.slides.make_everyone_happy.image}
        heading={data.slides.make_everyone_happy.title}
        description={data.slides.make_everyone_happy.description}
        jumpTarget="#sit-back-relax"
        onButtonClick={openModal}
      />
      <WhyWorkWithUs
        id="why-work-with-us"
        onButtonClick={openModal}
        subheading={data.why_work_with_us_subheading}
      />
      <SitBackRelax
        id="sit-back-relax"
        className={data.slides.sit_back_relax?.textColor}
        imageUrl={data.slides.sit_back_relax.image}
        heading={data.slides.sit_back_relax.title}
        description={data.slides.sit_back_relax.description}
        jumpTarget="#repeat-repeat"
        onButtonClick={openModal}
      />
      <RepeatRepeat
        id="repeat-repeat"
        className={data.slides.repeat?.textColor}
        imageUrl={data.slides.repeat.image}
        heading={data.slides.repeat.title}
        description={data.slides.repeat.description}
        jumpTarget="#catering-any-event"
        onButtonClick={openModal}
      />
      <CateringForAnyEvent
        id="catering-any-event"
        className="bg-white"
        heading={data.occasions.title}
        description={data.occasions.description}
      />
      <HowItWorks
        id="how-it-works-mobile"
        onButtonClick={openModal}
        items={data.how_it_works}
      />
      <HowItWorksLarge
        id="how-it-works"
        onButtonClick={openModal}
        items={data.how_it_works}
        image={data.how_it_works_image}
      />
      <Calculator
        id="calculator"
        cuisinePage={true}
        cateringMinimum={data.calculator.minimum}
        pricePerPerson={data.calculator.price_per_person}
        heading={data.calculator.heading}
        subheading={data.calculator.subheading}
        onButtonClick={openModal}
      />
      <CuisineTypes baseUrl='/food-truck-catering' showFooter={true} />
      <FeaturedClients onButtonClick={openModal} />
      <Faq id="faq" data={data} />
    </Layout>
  )
}

function Faq({ data, ...rest }) {
  return (
    <div className="bg-navy" {...rest}>
      <div className="container p-6">
        <div className="flex w-full">
          <div className="w-full py-16">
            <h2 className="text-white text-4xl leading-none font-platform text-center">
              Frequently Asked Questions
            </h2>
            {data?.faq?.map((q, key) => (
              <div key={key} className="w-full md:w-4/5 mb-2 mt-6">
                <h3 className="text-white text-3xl">{q.question}</h3>
                <div className={[styles.faq, 'text-white text-lg']}
                dangerouslySetInnerHTML={{
                  __html: q.answer,
                }}/>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
